import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { StorageAPIService, StorageListResult } from 'app/providers/storage-api.service';
import { EnableClassTelemetry } from 'app/services/otel.service';

import { SystemService } from 'app/services/system.service';
import { SemantriaPreferences } from '../bridge-api/preferences.service';
import {
  RealtimeService,
  RealtimeEventHandler,
  RealtimeConnection,
  RealtimeMessage,
} from '../realtime.service';
import { CollectionService } from './collection.service';
import { urlFor } from './utils';

export interface UploadErrorType {
  count: number;
  first_row: number;
  error_type: string;
  error_data: string;
}

export interface UploadResult {
  added: number;
  submitted: number;
  updated: number;
  errors: number;
  error_types: Array<UploadErrorType>;
}

export interface UploadColumn {
  column_name: string;
  field_name: string;
  transform?: string;
  samples: Array<string>;
}

export interface UploadMetadata {
  encoding: string;
  delimiter: string;
  columns: Array<UploadColumn>;
  timezone: string;
  groupby?: string;
  firstRecordAsHeader?: boolean;
}

export interface Upload {
  id: string;
  state: UploadState;
  file_name: string;
  message: string;
  created_at: Date;
  modified_at: Date;
  document_count: number;
  collection_name: string;
  asset_key: string;
  service_mode: string;
  error_mode: string;
  size_bytes: number;
  result: UploadResult;
  metadata: UploadMetadata;
  progress: number;
  projectId?: string;
}

export enum UploadState {
  NEW = 'NEW', // just uploaded, waiting to be inspected
  INSPECTED = 'INSPECTED', // we’ve read through the file and pulled out the preview rows, waiting for mapping
  MAPPED = 'MAPPED', // has been mapped waiting to be picked up for loading
  STARTED = 'STARTED', // loading has started
  COMPLETE = 'COMPLETE', // loading is complete (end state)
  CANCELLED = 'CANCELLED', // user cancelled loading (end state)
  DELETED = 'DELETED', // user deleted (this is async and the actual delete happens later, also end state)
  ERROR = 'ERROR', // something bad happened
}

export type UploadSort = 'auto';

export interface UploadSearchOptions {
  projectId: string;
  collectionName: string;
  sort: UploadSort;
  limit: number;
}

@EnableClassTelemetry()
@Injectable()
export class UploadService extends StorageAPIService {
  public static mapUpload(item: Upload): Upload {
    item.created_at = new Date(item.created_at);
    item.modified_at = new Date(item.modified_at);
    item.progress = 0;
    if (item.document_count > 0) {
      item.progress = (item.result.submitted + item.result.errors) / item.document_count;
    }
    return item;
  }
  constructor(
    public http: HttpClient,
    public systemService: SystemService,
    private preferencesService: SemantriaPreferences,
    private realtimeService: RealtimeService,
    private collectionService: CollectionService
  ) {
    super(http, systemService);
  }

  public on(namespace: string, fn: RealtimeEventHandler<Upload>): RealtimeConnection {
    return this.realtimeService.on(
      namespace,
      {
        upload: (data: RealtimeMessage<Upload>) => {
          UploadService.mapUpload(data.object);
          fn(data);
        },
      },
      'uploadService'
    );
  }

  public async updateMatadata(
    upload: Upload,
    reinspect: boolean,
    timezone: string = ''
  ): Promise<Upload> {
    if (timezone) {
      upload.metadata.timezone = timezone;
    } else if (this.preferencesService) {
      const preferences = await this.preferencesService.getPreferences();
      upload.metadata.timezone = preferences.timezone;
    }
    const result = await this.put<Upload>(
      urlFor(`/uploads/${upload.id}/metadata?reinspect=${reinspect}`, upload.projectId),
      upload.metadata
    );
    const mapped = UploadService.mapUpload(result);
    return mapped;
  }

  public async searchUploads(
    options: Partial<UploadSearchOptions>
  ): Promise<StorageListResult<Upload>> {
    const opts = Object.assign(
      {
        sort: 'auto',
        limit: 20,
      },
      options
    );
    let endpoint = '/uploads';
    if (opts.collectionName) {
      endpoint = `/collections/${opts.collectionName}/uploads`;
    }
    const result = await this.getPage<Upload>(urlFor(endpoint, opts.projectId), {
      detailed: 'true',
      'page-limit': opts.limit,
    });

    result.items = result.items.map(UploadService.mapUpload);

    return result;
  }

  public getAllUploads(projectId?: string): Promise<StorageListResult<Upload>> {
    return this.searchUploads({ projectId, limit: StorageAPIService.MAX_LIMIT });
  }

  public async getAllUploadsForCollection(
    collectionName: string
  ): Promise<StorageListResult<Upload>> {
    return this.searchUploads({ collectionName, limit: StorageAPIService.MAX_LIMIT });
  }

  public async getAllUploadsForProjectAndCollection(
    projectId: string,
    collectionName: string
  ): Promise<StorageListResult<Upload>> {
    return this.searchUploads({ projectId, collectionName, limit: StorageAPIService.MAX_LIMIT });
  }

  public async getUploadById(id: string, projectId?: string): Promise<Upload> {
    const result = await this.get<Upload>(urlFor(`/uploads/${id}`, projectId));

    const mapped = UploadService.mapUpload(result);
    return mapped;
  }

  public async deleteUpload(id: string, projectId: string, collectionId: string): Promise<void> {
    return this.delete<void>(`/projects/${projectId}/collections/${collectionId}/uploads/${id}/`);
  }

  public deleteUploadsForCollection(collectionName: string): Promise<void> {
    return this.delete<void>(`/collections/${collectionName}/uploads`);
  }

  public cancelUpload(id: string, projectId: string, collectionId: string): Promise<void> {
    return this.post<void>(
      `/projects/${projectId}/collections/${collectionId}/uploads/${id}/_cancel`
    );
  }

  // TODO: Need a real API
  public async getSocialUploads(projectId?: string): Promise<Upload[]> {
    const collections = await this.collectionService.getSocialCollections(projectId);
    const mockSocialUpload = collections.items.map((item) => {
      return {
        id: Math.floor(Math.random() * 1000).toString(),
        projectId,
        collection_name: item['id'],
        file_name: item.displayName + '.xlsx',
        size_bytes: 39556,
        asset_key: null,
        state: 'COMPLETE',
        created_at: new Date(),
        modified_at: new Date(),
        document_count: item.documentCount,
        metadata: {
          encoding: '',
          delimiter: null,
          quote: null,
          firstRecordAsHeader: true,
          columns: [
            {
              samples: ['8mt1tc', '7u22ya', '8toslj', '7tox6e', '88usn4'],
              column_name: 'ID',
              field_name: 'id',
            },
            {
              samples: [
                'Whats the ABV of my ho memade spiced whiskey?',
                'having wedding reception soon where can I buy liquor in bulks',
                'What to bring back to Ecuador (and what to drink while there)?',
                'Vintage Johhnie Waler Red value',
                'Recommend me an Arak!',
              ],
              column_name: 'Title',
              field_name: 'title',
            },
            {
              samples: [
                "So I make my spiced whiskey (recipe below) at home and I was wondering (roughly) what the alcohol % would be?\n\nIngredients:\n-Apples. Fuji, Golden Delicious, or anything you'd use for Apple pie\n-Whole cloves\n-Whole star anise\n-Whole nutmeg\n-Cinnamon sticks\n-Whiskey. I usually get a handle of the bottom shelf stuff and it works just fine for me. \n\nPrep:\n1) Chop up apples into 8 piece chunks\n2) Toast cloves and star anise in dry pan until fragrant\n2) Fill large mason jars with chopped apples, star anise, clove, and two cinnamon sticks. Grate fresh nutmeg on top\n3) Fill jars to top with whiskey\n4) Let sit for 48-72 hours flipping every 18.\n\nI know the apples absorb some of the alcohol and some of their juices may dilute the mix. Thoughts?\n\nSide note: This stuff is really good. I highly recommend making it. I also use the apples (after a lot of cooking) for Apple cobbler and the leftover spices to make my favorite cocktail simple syrup.",
                'NULL',
                'Ill be away volunteering in the rain forest while in Ecuador, and I was wondering, what kind of alcohol should I bring? Is there anything that the country is known for? Also, what would you recommend me bring back for friends? \n\n',
                'NULL',
                "Hello everyone, I'm a long time absinthe fan. Two of my favourites are Jade V.S. 1898 and La Clandestine Angelique. I want to branch out into the other anise spirits and Arak has seemed very interesting to me. Recommend me a bottle under or around £60 if you would be so kind.",
              ],
              column_name: 'Text',
              field_name: 'text',
            },
            {
              samples: [
                '2018-05-28 16:19:20',
                '2018-01-30 11:13:24',
                '2018-06-25 04:07:13',
                '2018-01-28 20:48:21',
                '2018-04-01 19:00:15',
              ],
              column_name: 'Date',
              field_name: 'createdAt',
            },
            {
              samples: ['Liquor', 'Liquor', 'Liquor', 'Liquor', 'Liquor'],
              column_name: 'Tags',
              field_name: 'tags',
            },
            {
              samples: [
                '0.6787428987231675',
                '0.765750489807866',
                '0.30072560067561227',
                '0.030948617680072177',
                '0.2832849016011828',
              ],
              column_name: 'MyRand',
              field_name: null,
            },
            {
              samples: ['compole', 'prorrhesis', 'delouse', 'nonstarter', 'pedipalpal'],
              column_name: 'MyWord1',
              field_name: null,
            },
            {
              samples: ['absorption', 'absorption', 'adviceful', 'adviceful', 'adviceful'],
              column_name: 'MyWord2',
              field_name: null,
            },
            {
              samples: ['0', '5', '8', '8', '9'],
              column_name: 'NPS',
              field_name: null,
            },
            {
              samples: ['3', '0', '6', '8', '3'],
              column_name: 'Price',
              field_name: null,
            },
            {
              samples: ['7', '8', '2', '7', '3'],
              column_name: 'Quality',
              field_name: null,
            },
            {
              samples: ['1', '3', '2', '4', 'NULL'],
              column_name: 'Availability',
              field_name: null,
            },
            {
              samples: ['9', '8', '5', '4', '9'],
              column_name: 'Nose',
              field_name: null,
            },
            {
              samples: ['1', '2', '7', '9', '1'],
              column_name: 'Proof',
              field_name: null,
            },
          ],
          timezone: 'Europe/Budapest',
        },
        result: {
          added: 100,
          updated: 0,
          submitted: 100,
          errors: 0,
          error_types: [],
        },
        message: 'Upload complete',
        service_mode: 'RELAXED',
        error_mode: 'RELAXED',
        version: 10,
        progress: 1,
        period_start: new Date(),
        period_end: new Date(),
        total_impressions: Math.floor(Math.random() * 100000),
        total_authors: Math.floor(Math.random() * 100000),
        language: 'English',
        sources: ['Social media', 'Blogs', 'Comments'],
        queries: ['Phrases', 'Symptoms', 'Medications'],
      } as unknown as Upload;
    });
    return new Promise((resolve) => resolve(mockSocialUpload));
  }
}
